import React, { useState, useEffect, useMemo } from 'react';
import { X, Truck, Package, Loader as Loader2, CircleAlert as AlertCircle, CircleCheck as CheckCircle, Bell, BellOff, ExternalLink, Sparkles } from 'lucide-react';
import type { ShopifyOrder } from '../../types/shopify';
import { formatCurrency } from '../../types/shopify';

interface FulfillmentModalProps {
  order: ShopifyOrder;
  carriers: { value: string; label: string }[];
  defaultCarrier: string;
  onClose: () => void;
  onSubmit: (trackingCode: string, carrier: string, notifyCustomer: boolean) => Promise<void>;
}

const CARRIER_PATTERNS: { carrier: string; patterns: RegExp[]; description: string }[] = [
  {
    carrier: 'PostNL',
    patterns: [
      /^3S[A-Z0-9]{11,13}$/i,
      /^[A-Z]{2}\d{9}NL$/i,
      /^\d{13,14}$/,
    ],
    description: 'PostNL tracking codes typically start with 3S or are 13-14 digits',
  },
  {
    carrier: 'DHL',
    patterns: [
      /^\d{10,12}$/,
      /^JJD\d{18}$/i,
      /^[A-Z]{3}\d{7}$/i,
    ],
    description: 'DHL tracking codes are usually 10-12 digits or start with JJD',
  },
  {
    carrier: 'DPD',
    patterns: [
      /^\d{14}$/,
      /^[0-9]{27}$/,
    ],
    description: 'DPD tracking codes are typically 14 or 27 digits',
  },
  {
    carrier: 'UPS',
    patterns: [
      /^1Z[A-Z0-9]{16}$/i,
      /^T\d{10}$/i,
      /^\d{26}$/,
    ],
    description: 'UPS tracking codes typically start with 1Z',
  },
  {
    carrier: 'FedEx',
    patterns: [
      /^\d{12,15}$/,
      /^\d{20,22}$/,
    ],
    description: 'FedEx tracking codes are 12-15 or 20-22 digits',
  },
  {
    carrier: 'GLS',
    patterns: [
      /^[A-Z0-9]{11,12}$/i,
    ],
    description: 'GLS tracking codes are typically 11-12 alphanumeric characters',
  },
  {
    carrier: 'Budbee',
    patterns: [
      /^BUD[A-Z0-9]+$/i,
    ],
    description: 'Budbee tracking codes start with BUD',
  },
];

const TRACKING_URL_TEMPLATES: Record<string, string> = {
  'PostNL': 'https://postnl.nl/tracktrace/?B={code}',
  'DHL': 'https://www.dhl.com/nl-en/home/tracking.html?tracking-id={code}',
  'DPD': 'https://tracking.dpd.de/status/en_US/parcel/{code}',
  'UPS': 'https://www.ups.com/track?loc=en_US&tracknum={code}',
  'FedEx': 'https://www.fedex.com/fedextrack/?trknbr={code}',
  'GLS': 'https://gls-group.eu/NL/en/parcel-tracking?match={code}',
  'Budbee': 'https://tracking.budbee.com/{code}',
};

function detectCarrier(trackingCode: string): string | null {
  const cleanCode = trackingCode.trim().toUpperCase();
  if (!cleanCode) return null;

  for (const { carrier, patterns } of CARRIER_PATTERNS) {
    for (const pattern of patterns) {
      if (pattern.test(cleanCode)) {
        return carrier;
      }
    }
  }

  return null;
}

function getTrackingUrl(carrier: string, trackingCode: string): string | null {
  const template = TRACKING_URL_TEMPLATES[carrier];
  if (!template) return null;
  return template.replace('{code}', encodeURIComponent(trackingCode.trim()));
}

const FulfillmentModal: React.FC<FulfillmentModalProps> = ({
  order,
  carriers,
  defaultCarrier,
  onClose,
  onSubmit,
}) => {
  const [trackingCode, setTrackingCode] = useState('');
  const [carrier, setCarrier] = useState(defaultCarrier);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notifyCustomer, setNotifyCustomer] = useState(true);
  const [autoDetected, setAutoDetected] = useState(false);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !loading) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [onClose, loading]);

  useEffect(() => {
    const detected = detectCarrier(trackingCode);
    if (detected && detected !== carrier) {
      setCarrier(detected);
      setAutoDetected(true);
      setTimeout(() => setAutoDetected(false), 2000);
    }
  }, [trackingCode]);

  const trackingUrl = useMemo(() => {
    if (!trackingCode.trim()) return null;
    return getTrackingUrl(carrier, trackingCode);
  }, [carrier, trackingCode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!trackingCode.trim()) {
      setError('Tracking code is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await onSubmit(trackingCode.trim(), carrier, notifyCustomer);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fulfill order');
      setLoading(false);
    }
  };

  const totalItems = order.line_items.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75"
          onClick={!loading ? onClose : undefined}
        />

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <form onSubmit={handleSubmit}>
            <div className="bg-gradient-to-r from-green-600 to-emerald-600 px-6 py-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                    <Truck className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">
                      Fulfill Order
                    </h3>
                    <p className="text-sm text-green-100">
                      {order.order_number}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  disabled={loading}
                  className="text-white/80 hover:text-white transition-colors disabled:opacity-50 p-1"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 px-6 py-6">
              <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-200 dark:border-gray-600">
                <div className="flex items-center gap-2 mb-3">
                  <Package className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Order Summary
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Items</p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      {totalItems} {totalItems === 1 ? 'item' : 'items'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Total</p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      {formatCurrency(order.total_price, order.currency)}
                    </p>
                  </div>
                  {order.customer_name && (
                    <div className="col-span-2">
                      <p className="text-xs text-gray-500 dark:text-gray-400">Customer</p>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {order.customer_name}
                      </p>
                    </div>
                  )}
                </div>

                {order.line_items.length <= 3 && (
                  <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600 space-y-2">
                    {order.line_items.map((item) => (
                      <div key={item.id} className="flex items-center justify-between text-sm">
                        <span className="text-gray-700 dark:text-gray-300 truncate flex-1 mr-2">
                          {item.title}
                        </span>
                        <span className="text-gray-500 dark:text-gray-400 flex-shrink-0">
                          x{item.quantity}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400 flex-shrink-0" />
                  <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Tracking Code <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={trackingCode}
                      onChange={(e) => setTrackingCode(e.target.value)}
                      disabled={loading}
                      placeholder="Enter tracking number"
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:opacity-50 font-mono"
                      autoFocus
                    />
                    {autoDetected && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1 text-green-600 dark:text-green-400 text-xs font-medium animate-pulse">
                        <Sparkles className="h-3 w-3" />
                        Auto-detected
                      </div>
                    )}
                  </div>
                  {trackingUrl && (
                    <a
                      href={trackingUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-2 inline-flex items-center gap-1 text-xs text-cyan-600 dark:text-cyan-400 hover:underline"
                    >
                      Preview tracking link
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Carrier
                  </label>
                  <div className="grid grid-cols-4 gap-2">
                    {carriers.slice(0, 7).map((c) => (
                      <button
                        key={c.value}
                        type="button"
                        onClick={() => setCarrier(c.value)}
                        disabled={loading}
                        className={`px-3 py-2 text-xs font-medium rounded-lg border transition-all ${
                          carrier === c.value
                            ? 'bg-green-100 dark:bg-green-900/30 border-green-500 text-green-700 dark:text-green-300 ring-2 ring-green-500/20'
                            : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-green-300 dark:hover:border-green-700'
                        } disabled:opacity-50`}
                      >
                        {c.label}
                      </button>
                    ))}
                    <select
                      value={carrier}
                      onChange={(e) => setCarrier(e.target.value)}
                      disabled={loading}
                      className={`px-3 py-2 text-xs font-medium rounded-lg border bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 ${
                        !carriers.slice(0, 7).some(c => c.value === carrier) ? 'ring-2 ring-green-500/20 border-green-500' : ''
                      }`}
                    >
                      <option value="">Other</option>
                      {carriers.map((c) => (
                        <option key={c.value} value={c.value}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                  <div className="flex items-center gap-2">
                    {notifyCustomer ? (
                      <Bell className="h-4 w-4 text-green-600 dark:text-green-400" />
                    ) : (
                      <BellOff className="h-4 w-4 text-gray-400" />
                    )}
                    <div>
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Notify Customer
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Send shipping notification email
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setNotifyCustomer(!notifyCustomer)}
                    disabled={loading}
                    className={`relative w-12 h-6 rounded-full transition-colors ${
                      notifyCustomer ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
                    } disabled:opacity-50`}
                  >
                    <span
                      className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                        notifyCustomer ? 'translate-x-6' : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-800/50 px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row gap-3 sm:justify-end">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="px-6 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl font-medium transition-colors hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || !trackingCode.trim()}
                className="px-6 py-2.5 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white rounded-xl font-medium transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-green-500/25"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Fulfilling...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4" />
                    Mark as Fulfilled
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default FulfillmentModal;
